<script setup lang="ts">
/**
 * The cells of a table, laid out as the table is.
 *
 * Works the way a spreadsheet does, because that is the tool every person who
 * edits a table of figures already knows: a handle at the head of each column
 * and each row selects the whole of it, the corner selects the table, and
 * putting the cursor in a cell selects that cell. Dragging from one cell to
 * another selects the block between them, and so does Shift with a click;
 * Shift with a click on a second row or column number takes every row or
 * column between the two. What is selected is shown on the cells themselves,
 * so somebody can see what a colour or a merge is about to land on before it
 * lands.
 *
 * Merged cells are drawn merged, from the same plan the page is drawn from,
 * so the grid has the shape of the printed table. Draws over the table's
 * column count rather than over each row's own length, so a table that arrived
 * ragged from an import still shows as a rectangle and every cell of it can be
 * reached.
 *
 * A selection made with the pointer is reported when the pointer is
 * released, not while it is down. The toolbar above the grid changes with the
 * selection, and a toolbar that grew mid-drag would push the grid down under
 * the pointer and hand the block to whichever cell slid beneath it.
 *
 * Reports selections and edits; changes nothing itself.
 */
import { useI18n } from 'vue-i18n'

import { computed, onBeforeUnmount, ref } from 'vue'

import type { TableSection } from '@/domain/section'
import {
  cellAt,
  covers,
  settleTarget,
  type CellPoint,
  type CellTarget,
  type RowRef,
} from '@/sections/table/edit'
import { drawnByLine, lineOf, planOf, rowOf, type DrawnCell } from '@/sections/table/spans'

const { t } = useI18n()

const props = defineProps<{
  section: TableSection
  target: CellTarget | null
}>()

const emit = defineEmits<{
  select: [target: CellTarget | null]
  edit: [row: RowRef, column: number, value: string]
}>()

/** Words and layout are CSRD's: cells can be selected and styled, not typed in. */
const locked = computed(() => Boolean(props.section.csrd))

const plan = computed(() => planOf(props.section))

const columns = computed(() => Array.from({ length: plan.value.columns }, (_, at) => at))

/** Each line with the cells drawn starting on it; the header's is the first. */
const lines = computed(() =>
  drawnByLine(plan.value).map((cells, line) => ({ line, row: rowOf(line), cells })),
)

const header = computed(() => lines.value[0] ?? { line: 0, row: 'header' as const, cells: [] })
const body = computed(() => lines.value.slice(1))

function pointOf(drawn: DrawnCell): CellPoint {
  return { row: rowOf(drawn.line), column: drawn.column }
}

/**
 * What is being chosen with the pointer, until it is released.
 *
 * Shown on the cells straight away, settled over merged cells the way it
 * will be once it is reported, so the block under the pointer is the block
 * that lands.
 */
const pending = ref<CellTarget | null>(null)

const shown = computed<CellTarget | null>(() =>
  pending.value ? settleTarget(props.section, pending.value) : props.target,
)

function isSelected(drawn: DrawnCell): boolean {
  return shown.value !== null && covers(shown.value, rowOf(drawn.line), drawn.column)
}

/** Whether a handle's own scope is the selection, for showing it pressed. */
function isHandleOn(target: CellTarget): boolean {
  const current = shown.value
  if (!current || current.scope !== target.scope) return false
  if (current.scope === 'row' && target.scope === 'row') return current.row === target.row
  if (current.scope === 'column' && target.scope === 'column') {
    return current.column === target.column
  }
  return current.scope === 'table'
}

function valueAt(drawn: DrawnCell): string {
  return cellAt(props.section, rowOf(drawn.line), drawn.column)?.value ?? ''
}

/** What the cell is and how it is set, as classes for its text field. */
function inputClasses(drawn: DrawnCell): (string | Record<string, boolean>)[] {
  const cell = cellAt(props.section, rowOf(drawn.line), drawn.column)
  return [
    {
      'grid__input--numeric': cell?.numeric === true,
      'grid__input--strong': cell?.strong === true,
    },
    cell?.align ? `grid__input--${cell.align}` : '',
  ]
}

/** A cell's own colours, shown in the grid so the grid looks like the page. */
function colourAt(drawn: DrawnCell): Record<string, string> {
  const cell = cellAt(props.section, rowOf(drawn.line), drawn.column)
  const style: Record<string, string> = {}
  if (cell?.fill) style['--cell-fill'] = cell.fill
  if (cell?.ink) style['--cell-ink'] = cell.ink
  return style
}

function span(value: number): number | undefined {
  return value > 1 ? value : undefined
}

function edit(drawn: DrawnCell, event: Event): void {
  emit('edit', rowOf(drawn.line), drawn.column, (event.target as HTMLInputElement).value)
}

// ── Choosing a block of cells ─────────────────────────────────────────────

/**
 * The cell a block is drawn from: the one the pointer last went down on, or
 * the one last typed into. Kept here rather than read off the selection,
 * because a settled selection is a rectangle and no longer says which of its
 * corners somebody started from.
 */
const origin = ref<CellPoint | null>(null)

/** Whether the pointer is down on a cell and could be drawing a block. */
let pressed = false

/** Whether it has left its first cell, so a block is being drawn. */
const drawing = ref(false)

/** The pointer up anywhere: whatever was chosen with it is now the selection. */
function release(): void {
  pressed = false
  drawing.value = false
  if (pending.value) emit('select', pending.value)
  pending.value = null
}

onBeforeUnmount(() => window.removeEventListener('pointerup', release))

/**
 * Down on a cell.
 *
 * With Shift, the block from the first cell to this one, and the text field
 * keeps its focus where it was: the press is choosing, not moving the cursor.
 * Otherwise the start of a possible drag, and anywhere in the cell puts the
 * cursor in its text, including the air a tall merged cell has around it.
 */
function press(event: MouseEvent, drawn: DrawnCell): void {
  if (event.button !== 0) return
  const field = event.target instanceof HTMLInputElement

  if (event.shiftKey && origin.value) {
    event.preventDefault()
    emit('select', { scope: 'range', from: origin.value, to: pointOf(drawn) })
    return
  }

  origin.value = pointOf(drawn)
  pressed = true
  pending.value = { scope: 'cell', ...pointOf(drawn) }
  window.removeEventListener('pointerup', release)
  window.addEventListener('pointerup', release, { once: true })

  if (!field) {
    event.preventDefault()
    ;(event.currentTarget as HTMLElement).querySelector('input')?.focus()
  }
}

/** The pointer, still down, reaching a cell: the block grows or shrinks to it. */
function reach(drawn: DrawnCell): void {
  const from = origin.value
  if (!pressed || !from) return
  const home = lineOf(from.row) === drawn.line && from.column === drawn.column
  if (home && !drawing.value) return

  if (!drawing.value) {
    drawing.value = true
    // The press began in a text field, which would otherwise go on selecting
    // its own letters under the pointer for the whole of the drag.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
    window.getSelection()?.removeAllRanges()
  }

  pending.value = home ? { scope: 'cell', ...from } : { scope: 'range', from, to: pointOf(drawn) }
}

/**
 * Arriving in a cell's text selects the cell and makes it where the next
 * block starts. Left to the release when the pointer brought the cursor here,
 * so a press is reported once, when it is over.
 */
function focusCell(drawn: DrawnCell): void {
  if (pressed) return
  origin.value = pointOf(drawn)
  emit('select', { scope: 'cell', ...pointOf(drawn) })
}

/** The row or column a Shift and a click on a second handle stretches from. */
const handleOrigin = ref<
  | { readonly scope: 'row'; readonly row: RowRef }
  | { readonly scope: 'column'; readonly column: number }
  | null
>(null)

function pickRow(event: MouseEvent, row: RowRef): void {
  const from = handleOrigin.value
  if (event.shiftKey && from?.scope === 'row' && from.row !== row) {
    emit('select', {
      scope: 'range',
      from: { row: from.row, column: 0 },
      to: { row, column: plan.value.columns - 1 },
    })
    return
  }
  handleOrigin.value = { scope: 'row', row }
  emit('select', { scope: 'row', row })
}

function pickColumn(event: MouseEvent, column: number): void {
  const from = handleOrigin.value
  if (event.shiftKey && from?.scope === 'column' && from.column !== column) {
    emit('select', {
      scope: 'range',
      from: { row: 'header', column: from.column },
      to: { row: rowOf(plan.value.lines - 1), column },
    })
    return
  }
  handleOrigin.value = { scope: 'column', column }
  emit('select', { scope: 'column', column })
}
</script>

<template>
  <div class="grid" :class="{ 'grid--drawing': drawing }" @keydown.esc="emit('select', null)">
    <table class="grid__table">
      <thead>
        <tr class="grid__handles">
          <th class="grid__corner">
            <button
              type="button"
              class="handle handle--corner"
              :class="{ 'handle--on': isHandleOn({ scope: 'table' }) }"
              :title="t('inspector.table.selectTable')"
              @click="emit('select', { scope: 'table' })"
            >
              <svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true">
                <path
                  d="M2.5 2.5h11v11h-11zM2.5 7h11M7 2.5v11"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                />
              </svg>
              <span class="visually-hidden">{{ t('inspector.table.selectTable') }}</span>
            </button>
          </th>
          <th v-for="column in columns" :key="column" class="grid__handle-cell">
            <button
              type="button"
              class="handle"
              :class="{ 'handle--on': isHandleOn({ scope: 'column', column }) }"
              :title="t('inspector.table.selectColumn', { number: column + 1 })"
              @click="pickColumn($event, column)"
            >
              {{ column + 1 }}
            </button>
          </th>
        </tr>
      </thead>
      <!-- The header's line sits with the body rather than in the head above:
           a header cell can be merged down into the body, and a merge cannot
           cross from one group of rows into another. -->
      <tbody>
        <tr>
          <th class="grid__row-head">
            <button
              type="button"
              class="handle"
              :class="{ 'handle--on': isHandleOn({ scope: 'row', row: 'header' }) }"
              :title="t('inspector.table.selectHeader')"
              @click="pickRow($event, 'header')"
            >
              {{ t('inspector.table.headerHandle') }}
            </button>
          </th>
          <th
            v-for="drawn in header.cells"
            :key="drawn.column"
            class="grid__cell grid__cell--head"
            :class="{ 'grid__cell--selected': isSelected(drawn) }"
            :style="colourAt(drawn)"
            :colspan="span(drawn.colspan)"
            :rowspan="span(drawn.rowspan)"
            @mousedown="press($event, drawn)"
            @pointerenter="reach(drawn)"
          >
            <input
              class="grid__input grid__input--head"
              :class="inputClasses(drawn)"
              :value="valueAt(drawn)"
              :placeholder="t('inspector.table.columnPlaceholder', { number: drawn.column + 1 })"
              @focus="focusCell(drawn)"
              :readonly="locked"
              @input="edit(drawn, $event)"
            />
          </th>
        </tr>
        <tr v-for="line in body" :key="line.line">
          <th class="grid__row-head">
            <button
              type="button"
              class="handle"
              :class="{ 'handle--on': isHandleOn({ scope: 'row', row: line.row }) }"
              :title="t('inspector.table.selectRow', { number: line.line })"
              @click="pickRow($event, line.row)"
            >
              {{ line.line }}
            </button>
          </th>
          <td
            v-for="drawn in line.cells"
            :key="drawn.column"
            class="grid__cell"
            :class="{ 'grid__cell--selected': isSelected(drawn) }"
            :style="colourAt(drawn)"
            :colspan="span(drawn.colspan)"
            :rowspan="span(drawn.rowspan)"
            @mousedown="press($event, drawn)"
            @pointerenter="reach(drawn)"
          >
            <input
              class="grid__input"
              :class="inputClasses(drawn)"
              :value="valueAt(drawn)"
              @focus="focusCell(drawn)"
              :readonly="locked"
              @input="edit(drawn, $event)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.grid {
  overflow: auto;
  border: 1px solid var(--hairline);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

/* While a block is drawn, nothing under the pointer selects text. */
.grid--drawing {
  cursor: cell;
  user-select: none;
}

.grid--drawing .grid__input {
  cursor: cell;
}

.grid__table {
  border-collapse: collapse;
  width: 100%;
}

.grid__corner,
.grid__row-head,
.grid__handle-cell {
  padding: 0;
  background: var(--panel);
}

.grid__corner,
.grid__row-head {
  width: 24px;
}

.grid__handle-cell {
  border-left: 1px solid var(--hairline);
}

.grid__handles th {
  border-bottom: 1px solid var(--hairline);
}

.handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 20px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--ink-faint);
  font: inherit;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.grid__row-head .handle {
  height: 100%;
  min-height: 28px;
}

.handle:hover {
  background: var(--panel-hover);
  color: var(--ink);
}

.handle--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}

.grid__cell {
  position: relative;
  padding: 0;
  min-width: 84px;
  border-top: 1px solid var(--hairline);
  border-left: 1px solid var(--hairline);
  /* The cell's own colours, where it has them, so the grid reads like the
     page. A cell nobody coloured shows the panel through. */
  background: var(--cell-fill, transparent);
  cursor: text;
}

.grid__cell--head {
  border-top: 0;
}

/* A merged cell runs over rows as it does on the page, and its text sits in
   the middle of it, where the page puts it too. */
.grid__cell[rowspan] {
  vertical-align: middle;
}

/* Selection is a ring inside the cell rather than a border on it, so the
   rules between cells stay where they are and the table does not jitter. It
   is drawn under the input: over it, its tint hid what the cell says. */
.grid__cell--selected::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  box-shadow: inset 0 0 0 2px var(--accent);
  background: var(--accent-quiet);
}

.grid__input {
  position: relative;
  z-index: 1;
  width: 100%;
  padding: var(--sp-3) var(--sp-4);
  border: 0;
  background: transparent;
  color: var(--cell-ink, var(--ink));
  font: inherit;
  font-size: var(--text-sm);
  font-variant-numeric: tabular-nums;
}

.grid__input:focus {
  outline: none;
}

.grid__input--head {
  font-weight: 600;
}

.grid__input--numeric {
  text-align: right;
}

.grid__input--strong {
  font-weight: 600;
}

.grid__input--left {
  text-align: left;
}

.grid__input--center {
  text-align: center;
}

.grid__input--right {
  text-align: right;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
